import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms'
import {Observable, timer} from 'rxjs'
import {
  Answer,
  AnswerFormGroup,
  AnswerModelType,
  Question,
  SelectOption,
  SelectOptions,
  SingleAnswerControls,
  SingleAnswerValue,
} from './dynamic-form'
import {map, take, tap} from 'rxjs/operators'

export class SingleAnswerFormGroup extends UntypedFormGroup implements AnswerFormGroup {
  readonly type = AnswerModelType.SINGLE_ANSWER
  readonly question: Question
  readonly additionalInformationRequired$: Observable<boolean>
  declare controls: SingleAnswerControls

  constructor(question: Question, defaultAnswer?: Answer) {
    super({
      answer: new UntypedFormControl(
        defaultAnswer?.answerValue ?? (defaultAnswer?.answerIds?.length ? defaultAnswer?.answerIds[0] : undefined),
        question.required ? [Validators.required] : [],
      ),
      additionalInformation: new UntypedFormControl(
        {value: defaultAnswer?.additionalInformation, disabled: true},
        Validators.required,
      ),
    })

    this.question = question
    this.additionalInformationRequired$ = this.controls.answer.valueChanges.pipe(
      map(value => this.isAdditionalInfoRequired(question, value)),
      tap(required =>
        required ? this.controls.additionalInformation.enable() : this.controls.additionalInformation.disable(),
      ),
    )
  }

  toAnswer(): Answer {
    const result: Answer = {
      questionId: this.question.id,
      answerIds: this.isSelectOptions() && this.controls.answer.value ? [this.controls.answer.value] : undefined,
      answerValue: this.isSelectOptions() ? undefined : this.controls.answer.value?.toString(),
    }
    if (this.controls.additionalInformation.enabled) {
      result.additionalInformation = this.controls.additionalInformation.value
    }
    return result
  }

  patchValue(value: SingleAnswerValue, options?: {onlySelf?: boolean; emitEvent?: boolean}) {
    //there's a slight issue with directly patching the value as it doesn't trigger valueChanges of the form
    //doing it with the timer seems to do the trick.
    timer(0)
      .pipe(take(1))
      .subscribe(() => super.patchValue(value, options))
  }

  private isSelectOptions() {
    return Array.isArray(this.question.options) && this.question.options.length
  }

  private isAdditionalInfoRequired(question: Question, currentValue: unknown): boolean {
    const selectedOption = this.getSelectedOptionByKey(question, currentValue)
    return !!selectedOption?.requireAdditionalInformation
  }

  private getSelectedOptionByKey(question: Question, key: unknown): SelectOption | undefined {
    if (!Array.isArray(question.options) || !key) {
      return undefined
    }
    return (question.options as SelectOptions).find(o => o.key === key)
  }
}
